import { postToAgents } from "./PlaybookApi";
import { SKILL_BRIEF_FIELD_LABELS } from "../constants/PlaybookBriefs";

const SkillBuildService = {
	/**
	 * Opens a conversation with the skill development guide for writing a new skill, starting
	 * from a category, by its id, when the person came from one.
	 *
	 * @param {Object} params { categoryId }
	 * @return {Promise<Object>} the skill conversation
	 */
	startSkillBuild({ categoryId }) {
		return postToAgents({ operationId: "startSkillBuild", body: { categoryId } });
	},

	/**
	 * A skill conversation with its brief and the draft skill as it stands.
	 *
	 * @param {Object} params { sessionId }
	 * @return {Promise<Object>}
	 */
	getSkillBuild({ sessionId }) {
		return postToAgents({ operationId: "getSkillBuild", body: { sessionId } });
	},

	/**
	 * Sends a message to the skill development guide; resolves to the updated conversation.
	 *
	 * @param {Object} params { sessionId, text }
	 * @return {Promise<Object>}
	 */
	sendSkillBuildMessage({ sessionId, text }) {
		return postToAgents({ operationId: "sendSkillBuildMessage", body: { sessionId, text } });
	},

	/**
	 * Starts a skill conversation from a procedure the person already has written down.
	 *
	 * @param {Object} params { sessionId, procedure }
	 * @return {Promise<Object>}
	 */
	importSkillBuildProcedure({ sessionId, procedure }) {
		return postToAgents({ operationId: "importSkillBuildProcedure", body: { sessionId, procedure } });
	},

	/**
	 * Records that a skill conversation's draft was saved as a skill, and ends it.
	 *
	 * @param {Object} params { sessionId, skillId }
	 * @return {Promise<Object>}
	 */
	finishSkillBuild({ sessionId, skillId }) {
		return postToAgents({ operationId: "finishSkillBuild", body: { sessionId, skillId } });
	},

	/**
	 * Plain labels for a skill brief's fields, in the order the skill build screen shows them.
	 *
	 * @return {Array} [{ key, label }]
	 */
	skillBriefFields() {
		return SKILL_BRIEF_FIELD_LABELS;
	},

	/**
	 * What the skill editor starts with when a person reviews a guide's draft: the draft's text
	 * and tools, its reads-first tools as the editor keeps them, and the brief's category when it
	 * is one the firm has. A new category the guide named is not one yet, so the person picks.
	 *
	 * @param {Object} params
	 * @param {Object} params.session a skill conversation from getSkillBuild
	 * @return {Object} { categoryId, name, description, body, toolNames, reads, changeNote }
	 */
	skillDraftForEditor({ session }) {
		const draft = session.draftSkill;
		const prefill = {
			categoryId: session.category ? session.category.id : null,
			name: draft.name,
			description: draft.description,
			body: draft.body,
			toolNames: [...draft.toolNames],
			reads: (draft.readFirstToolNames || []).map(toolName => ({ tool: toolName })),
			changeNote: "",
		};
		return prefill;
	},
};

export default SkillBuildService;
